import { toast } from 'sonner'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateTracker, extractErrorMessage } from '@/queries/ticketMutations'
import { isAdminViewer } from '@/lib/scope'
import type { TrackerTicket } from '@/types/tracker'
import type { ViewerRelation } from '@/types/ticket'

// Item 4 of the issue-detail rebuild: the Impact half of TrackerPanel's old
// Row list, split out into its own section so it can sit below Description
// per the mockup (the property band above it only carries Status/Priority/
// Progress/Category/Expected). Same gating and mutation as before — see
// TrackerImpactSection.test.tsx, migrated 1:1 from TrackerPanel's suite.
export function TrackerImpactSection({
  ticket,
  viewerRelation,
}: {
  ticket: TrackerTicket
  viewerRelation: ViewerRelation
}) {
  const isStaff = isAdminViewer(viewerRelation)
  const updateTracker = useUpdateTracker(ticket._id)
  const { tracker } = ticket

  if (!isStaff && !tracker.impact) return null

  const patchTracker = async (input: Parameters<typeof updateTracker.mutateAsync>[0]) => {
    try {
      await updateTracker.mutateAsync(input)
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update tracker'))
    }
  }

  return (
    <section className="mb-[22px]">
      <h2 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Impact</h2>
      {isStaff ? (
        // Uncontrolled + remounted on the server value's own change (key):
        // buffers keystrokes locally and commits once on blur, rather than
        // firing a PATCH per character.
        <Textarea
          key={tracker.impact ?? ''}
          defaultValue={tracker.impact ?? ''}
          aria-label="Tracker impact"
          rows={2}
          className="w-full rounded-lg border-none bg-muted px-[18px] py-[14px] text-etn-control text-etn-text-cell"
          disabled={updateTracker.isPending}
          onBlur={e => {
            const value = e.target.value
            if (value !== (tracker.impact ?? '')) patchTracker({ impact: value })
          }}
        />
      ) : (
        <div className="rounded-lg bg-muted px-[18px] py-[14px] text-etn-control text-etn-text-cell">{tracker.impact}</div>
      )}
    </section>
  )
}
